import React, { useState } from "react";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { formatNumberCurrency } from "./Utils";
import { descreverCambio } from "../Utils/cambioJanela";
import { compararValores, motivoTexto, selecionadosDaCotacao } from "../Utils/previewValores";
import { QUOTE_STATUS_F2 } from "../Constants/quote";
import { previewValoresDisponivel } from "../Store/Actions/Quote";

/**
 * "Conferir tarifa de hoje" — consulta sob demanda numa cotação APROVADA.
 *
 * O QUE ESTE BOTÃO RESPONDE, e o que ele NÃO responde (corrigido em 05/09):
 *
 * O câmbio NÃO é a pergunta em aberto. O `/quote/retrieve` já recomputa `precoBRL` com a taxa do
 * dia a cada abertura, então o valor em reais que o painel mostra já é o de hoje. O que ninguém
 * atualiza é a TARIFA: ela fica gravada como estava na aprovação, e a consulta ao fornecedor só
 * acontece dentro do proceed-reservation — depois de a reserva existir, passado o ponto sem volta.
 *
 * Como os dois lados usam o mesmo câmbio, a diferença exibida é só de tarifa. É a única incógnita
 * que sobra, e é a que faz o operador descobrir tarde demais.
 *
 * NÃO existe em `reservada` — e o BE também removeu esse caminho em 05/09, devolvendo 409: ali a
 * tarifa está travada e o câmbio já vem vivo do retrieve, então o preview daria exatamente o número
 * que a tela já mostra. Pior, em item já pago o congelamento é por RESERVA, e recomputar pela taxa
 * de hoje contradiria o painel — reintroduzindo por uma porta nova o bug que aquele congelamento
 * existe para impedir.
 *
 * SOB DEMANDA, nunca automático: cada consulta custa uma chamada ao fornecedor por hotel. O BE
 * cacheia 60s por cotação, então reabrir o painel não redispara.
 *
 * LEITURA PURA: o resultado NUNCA sobrescreve os valores aprovados — fica ao lado deles. O backend
 * já teve o bug de re-cotar fora de hora e sobrescrever preço e disponibilidade de cotação
 * encerrada, chegando a marcar como indisponível um item reservado e pago. (O teste deles agora
 * fotografa as tabelas antes e depois e compara, então isso não volta em silêncio.)
 */
const PreviewValoresToggle = ({ quote, onPreview }) => {
	const [aberto, setAberto] = useState(false);
	const [carregando, setCarregando] = useState(false);
	const [erro, setErro] = useState("");
	const [comparacao, setComparacao] = useState(null);

	// SÓ em `aprovada` — ver o docblock. Em `reservada` não há incógnita: tarifa travada e câmbio já
	// vivo no retrieve. Em `ganha`/`parcialmente_ganha`/`perdida` está congelado e o BE recusa com 409.
	const aplicavel = quote?.status === QUOTE_STATUS_F2.APROVADA && previewValoresDisponivel();
	if (!aplicavel) return null;

	const aprovados = selecionadosDaCotacao(quote);

	const consultar = () => {
		if (aberto) { setAberto(false); return; }
		setCarregando(true); setErro("");
		Promise.resolve(onPreview(quote.id))
			.then((res) => {
				// Resposta sem item nenhum é FALHA DA CONSULTA, não "o fornecedor não respondeu por item".
				// Sem esta guarda, um payload vazio virava N avisos de "não foi possível consultar" — que
				// é indistinguível de uma falha real do fornecedor e mandou o owner investigar o BE por um
				// defeito que era do mock (05/09).
				if (!res || !Array.isArray(res.itens) || !res.itens.length) {
					setErro("A consulta não retornou nenhum item. Tente novamente; se persistir, avise o suporte.");
					return;
				}
				const c = compararValores(aprovados, res);
				if (!c) { setErro("Não foi possível comparar os valores agora."); return; }
				setComparacao(c); setAberto(true);
			})
			// O BE explica o motivo no 409 (PREVIEW_NAO_APLICAVEL distingue `reservada` de encerrada).
			// Trocar isso por um "tente novamente" generico devolveria o operador ao escuro — e o botao
			// so aparece em `aprovada`, entao se este erro chegar e porque o status mudou embaixo dele.
			.catch((e) => setErro(e?.response?.data?.errorMessage || e?.response?.data?.message
				|| "Não foi possível consultar a tarifa de hoje. Tente novamente."))
			.finally(() => setCarregando(false));
	};

	const cor = (d) => (d > 0 ? "#B3261E" : d < 0 ? "#2E9E5B" : "#6B6B6B");
	const seta = (d) => (d > 0 ? "pi pi-arrow-up" : d < 0 ? "pi pi-arrow-down" : "pi pi-minus");
	const comSinal = (v) => `${v > 0 ? "+" : ""}${formatNumberCurrency(v, "BRL")}`;
	const nomes = (lista) => lista.map((i) => i.hotel).filter(Boolean).join(", ");

	return (
		// Mesmo peso visual do "Ver a cotação original": os dois são conferência opcional, não ação
		// principal. Botão sólido aqui competia com o "Prosseguir com reserva", que é o que importa.
		// A margem inferior existe porque, fechado, ele encostava no bloco das opções de estadia.
		<div style={{ marginTop: 10, marginBottom: aberto ? 10 : 18 }}>
			<Button text plain style={{ padding: "2px 0", color: "#814DE5", fontSize: "0.82rem" }}
				label={aberto ? "Ocultar tarifa de hoje" : "Conferir tarifa de hoje"}
				icon={aberto ? "pi pi-eye-slash" : "pi pi-refresh"}
				loading={carregando} disabled={carregando || !aprovados.length}
				onClick={consultar}
				tooltip="Consulta tarifa e disponibilidade ao vivo no fornecedor. Não altera a cotação."
				tooltipOptions={{ position: "top" }} />

			{erro ? <Message severity="error" style={{ width: "100%", marginTop: 8, justifyContent: "flex-start" }} text={erro} /> : null}

			{aberto && comparacao ? (
				<div style={{ marginTop: 10, border: "1px solid #E6DEF7", borderRadius: 10, padding: 12, background: "#FAF7FF" }}>
					<div style={{ fontSize: "0.78rem", color: "#6B6B6B", marginBottom: 8 }}>
						<i className="pi pi-info-circle" /> Consulta feita agora, apenas para conferência — <strong>a cotação não foi alterada</strong>. O câmbio de hoje já está aplicado nos <strong>dois</strong> lados, então a diferença abaixo é <strong>só de tarifa</strong>.
					</div>

					{comparacao.cambio ? (
						<div style={{ fontSize: "0.78rem", color: "#4A2E7A", marginBottom: 10 }}>
							<i className="pi pi-dollar" /> {descreverCambio(comparacao.cambio)?.texto}
						</div>
					) : null}

					{/* Os três motivos de recusa do BE, em avisos SEPARADOS e com pesos diferentes.
					    Juntá-los faria o operador tratar um timeout de fornecedor como hotel esgotado. */}
					{comparacao.indisponiveis.length ? (
						<Message severity="warn" style={avisoStyle}
							text={`${comparacao.indisponiveis.length} item(ns) não está(ão) mais disponível(is) no fornecedor: ${nomes(comparacao.indisponiveis)}. A reserva desse(s) trecho(s) vai falhar — refaça a cotação antes de prosseguir.`} />
					) : null}

					{/* Tarifa existe, mas o booking recusa pela política de cancelamento (BUG-004).
					    Sem separar, a tela diria "disponível" para item que o proceed vai negar. */}
					{comparacao.bloqueados.length ? (
						<Message severity="warn" style={avisoStyle}
							text={`${comparacao.bloqueados.length} item(ns) não pode(m) ser reservado(s) agora: ${nomes(comparacao.bloqueados)}. A tarifa existe, mas a política de cancelamento impede a reserva neste momento.`} />
					) : null}

					{/* "Não sei" — nunca vermelho, nunca contado como mudança. */}
					{comparacao.desconhecidos.length ? (
						<Message severity="info" style={avisoStyle}
							text={`Não foi possível consultar ${comparacao.desconhecidos.length} item(ns) agora (${nomes(comparacao.desconhecidos)}). Isso não quer dizer que ficaram indisponíveis — tente novamente em instantes.`} />
					) : null}

					{comparacao.itens.map((i) => (
						<div key={i.id} style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap", padding: "6px 0", borderTop: "1px dashed #E6DEF7" }}>
							<span style={{ flex: 1, minWidth: 140, fontSize: "0.85rem", color: "#2E0249" }}>{i.hotel}</span>
							<span style={{ fontSize: "0.8rem", color: "#8A8A8A", textDecoration: i.situacao === "indisponivel" ? "line-through" : "none" }}>
								{formatNumberCurrency(i.naTelaBRL, "BRL")}
							</span>
							{i.situacao === "ok" ? (
								<>
									<i className="pi pi-arrow-right" style={{ fontSize: "0.7rem", color: "#B9B9B9" }} />
									<span style={{ fontWeight: 700, color: "#2E0249" }}>{formatNumberCurrency(i.hojeBRL, "BRL")}</span>
									<span style={{ fontSize: "0.8rem", color: cor(i.deltaBRL) }}>
										<i className={seta(i.deltaBRL)} style={{ fontSize: "0.7rem" }} /> {comSinal(i.deltaBRL)}
									</span>
									{/* Em `reservada` a tarifa NÃO deveria mudar: sinalizar separa "o dólar
									    subiu" de "o fornecedor mexeu no preço", que são problemas diferentes. */}
									{i.mudouTarifa ? <span style={{ fontSize: "0.72rem", color: "#B26A00" }}>tarifa alterada</span> : null}
								</>
							) : (
								<span style={{ fontSize: "0.78rem", color: i.situacao === "desconhecido" ? "#6B6B6B" : "#B3261E", fontWeight: i.situacao === "desconhecido" ? 400 : 600 }}>
									{motivoTexto(i)}
								</span>
							)}
						</div>
					))}

					<div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap", paddingTop: 10, marginTop: 4, borderTop: "2px solid #E6DEF7" }}>
						<strong style={{ flex: 1, minWidth: 140, color: "#2E0249" }}>Total <span style={{ fontWeight: 400, fontSize: "0.72rem", color: "#8A8A8A" }}>(nesta tela → conferido agora)</span></strong>
						<span style={{ fontSize: "0.8rem", color: "#8A8A8A" }}>{formatNumberCurrency(comparacao.totalNaTelaBRL, "BRL")}</span>
						<i className="pi pi-arrow-right" style={{ fontSize: "0.7rem", color: "#B9B9B9" }} />
						<strong style={{ color: "#814DE5" }}>{formatNumberCurrency(comparacao.totalHojeBRL, "BRL")}</strong>
						<span style={{ color: cor(comparacao.totalDeltaBRL), fontWeight: 600 }}>
							<i className={seta(comparacao.totalDeltaBRL)} style={{ fontSize: "0.75rem" }} /> {comSinal(comparacao.totalDeltaBRL)}
							{comparacao.totalDeltaPct !== null ? ` (${comparacao.totalDeltaPct > 0 ? "+" : ""}${comparacao.totalDeltaPct.toFixed(1)}%)` : ""}
						</span>
					</div>

					{/* Sem este aviso o total menor pareceria boa notícia, quando é item faltando. */}
					{comparacao.parcial ? (
						<div style={{ fontSize: "0.75rem", color: "#B26A00", marginTop: 6 }}>
							<i className="pi pi-exclamation-triangle" /> O total de hoje cobre apenas os itens reserváveis agora — não é comparável ao total aprovado.
						</div>
					) : null}
				</div>
			) : null}
		</div>
	);
};

const avisoStyle = { width: "100%", marginBottom: 10, justifyContent: "flex-start", alignItems: "flex-start", textAlign: "left" };

export default PreviewValoresToggle;
